import { Autocomplete, TextField } from '@mui/material'

import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import React, { ReactElement } from 'react'
import FormRow from 'ui/components/FormRow/FormRow'

import { getHelpTextPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { useAuth } from '../AuthContext/AuthContext'

import styles from './DigitalTwinSettingsAutocompleteRow.module.less'

type DeviationPropertyChoice = {
  value: number | string
  name: string
  unitId?: string
}

type DeviationSettingsAutocompleteRowProps = {
  label: string
  id: string
  value: DeviationPropertyChoice | null
  defaultValue?: string | number
  onChange: (value?: string) => void
  choices: DeviationPropertyChoice[]
  disabled?: boolean
  required?: boolean
}

export function DigitalTwinSettingsAutocompleteRow({
  label,
  id,
  onChange,
  choices,
  disabled = false,
  value,
  required,
}: DeviationSettingsAutocompleteRowProps): ReactElement {
  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  return (
    <FormRow>
      <div className={styles.DigitalTwinSettingsAutocompleteRow_Container}>
        <Autocomplete
          isOptionEqualToValue={(option: DeviationPropertyChoice, values: DeviationPropertyChoice) =>
            option.value === values?.value
          }
          // Setting inputValue without disabled means you cannot type in the textfield,
          // so the autocomplete feature won't work. So we only set value when disabled = true.
          inputValue={disabled && value?.name ? value.name : undefined}
          // We use value=null to clear the component, but Autocomplete doesn't like that.
          // If we switch to using undefined, we get an error saying
          // "A component is changing an uncontrolled Autocomplete to be controlled".
          // @ts-ignore
          value={value}
          disableClearable={true}
          id={id}
          options={choices}
          renderOption={({ key, ...props }, option) => {
            if (option.value) {
              const helpText =
                typeof option.value === 'string' ? getHelpTextPrettyName(option.value, digitalTwin?.translations) : '' //only show a helptext if it exists for the value
              return (
                <li key={key} {...props}>
                  <div className={styles.DigitalTwinSettingsAutocompleteRow_OptionContainer}>
                    <span className={styles.DigitalTwinSettingsAutocompleteRow_OptionContainer_Title}>
                      {option.name || ''}
                    </span>
                    <div className={styles.DigitalTwinSettingsAutocompleteRow_OptionContainer_Helptext}>
                      <i className={styles.DigitalTwinSettingsAutocompleteRow_OptionContainer__small}>{helpText}</i>
                    </div>
                  </div>
                </li>
              )
            }
            return (
              <li key={key} {...props}>
                {option.name || ''}
              </li>
            )
          }}
          getOptionLabel={(option) => option.name || ''}
          onChange={(_e, v) => onChange(v?.value ? `${v?.value}` : undefined)}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="normal"
              name={id}
              required={required}
              slotProps={{
                inputLabel: {
                  // Raised while a value is chosen; otherwise left to the field, which raises it on focus or
                  // typed text. Forcing it down then let the label cover what the user types.
                  shrink: value?.name ? true : undefined,
                },
                select: {
                  MenuProps: {
                    className: styles.PropertyMenu_Menu,
                  },
                },
              }}
            />
          )}
          fullWidth
        />
      </div>
    </FormRow>
  )
}
